import MaterialIcon from '@components/common/MaterialIcon';
import CompanyMemberForm from '@components/company/forms/CompanyMemberForm';
import CreateCompanyForm from '@components/company/forms/CreateCompanyForm';
import {
  PROVISION_STATUS_ROUTE,
  SELECT_CUSTOMER_ROUTE,
} from '@constants/routes';
import type { CreateCompanyDTO } from '@conversed/rest/src/contracts/company/company.contract';
import { DEFAULT_ROLES } from '@conversed/shared/src/central/constants/permissions';
import { useCompanySettingsActions } from '@hooks/settings/company/useCompanyActions';
import conversedLogo from '@images/logo-black.svg';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import GeneralSettings from '../settings-page/GeneralSettings';

export const NewCustomerPage: React.FC = () => {
  const [step, setStep] = useState(1);
  const [company, setCompany] = useState<CreateCompanyDTO['company'] | null>(
    null,
  );
  const { createCompany } = useCompanySettingsActions();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formLabel, setFormLabel] = useState(t('new-customer.add-customer'));

  const handleCompanySubmit = useCallback(
    (data: CreateCompanyDTO['company']) => {
      setCompany(data);
      setStep(2);
      setFormLabel(t('new-customer.add-company-user'));
    },
    [t],
  );

  const handleBack = useCallback(() => {
    setStep(1);
    setFormLabel(t('new-customer.add-customer'));
  }, [t]);

  const handleCompanyMemberSubmit = useCallback(
    async (companyMember?: CreateCompanyDTO['member']) => {
      if (!company) {
        // TODO: Handle case
        return;
      }

      const member = !companyMember
        ? // If the company member is not provided, leave empty (Skip button implementation)
          undefined
        : {
            ...companyMember,
            roles: [DEFAULT_ROLES.AI_TRAINER_ROLE],
          };

      const companyId = await createCompany({
        company,
        member,
      });

      if (!companyId) {
        return;
      }

      // * A provisioned company goes straight to its live status curtain; a
      // * manually created one is already complete, so it returns to the selector.
      if (company.provision) {
        navigate(generatePath(PROVISION_STATUS_ROUTE, { companyId }));

        return;
      }

      navigate(SELECT_CUSTOMER_ROUTE);
    },
    [createCompany, company, navigate],
  );

  return (
    <div className='w-full h-dvh text-gray-600'>
      <div className='m-2'>
        <MaterialIcon icon='close' onClick={() => navigate(-1)} />
      </div>
      <div className='w-full flex justify-center items-center flex-col h-[calc(100%-100px)]'>
        <img src={conversedLogo} alt='company-logo' />
        <h3 className='w-full flex justify-center items-center text-4xl font-medium !text-primary-base m-10'>
          {formLabel}
        </h3>
        <div className='flex justify-center w-[calc(100%-40px)] max-w-[520px] overflow-auto px-2'>
          {step === 1 && <CreateCompanyForm onSubmit={handleCompanySubmit} />}
          {step === 2 && (
            <CompanyMemberForm
              onSubmit={handleCompanyMemberSubmit}
              onBack={handleBack}
            />
          )}
        </div>
      </div>
      <div className='copyright text-left text-xs font-normal leading-4 ml-4 text-gray-500 h-[50px] flex items-center'>
        {t('general.copyright.label', {
          date: new Date().getFullYear().toString(),
        })}
      </div>
    </div>
  );
};

export default GeneralSettings;
